import {
  DuskbreakersIcon,
  Ellipse,
  EllipseCut,
  ShapeHorizontal,
  ShapeVertical,
  SquaresShape,
} from 'components/Icons/Icons';
import React, { useEffect, useState } from 'react';
import styles from './PreviewCard.module.scss';
import cls from 'classnames';
import { LogoBig } from 'components/TraitCard/Logo';
import { AnimatePresence, m } from 'framer-motion';
import gribblePlaceHolder from './gribble-placeholder.png';

type PreviewCardProps = CardProps & {
  image: string;
  headerDescription: string;
  headerTitle: string;
  collectionFooter: string;
  flipped?: boolean;
  backside?: React.ReactNode;
};

type CardProps = {
  className?: string;
};

export const PreviewCard = ({
  headerDescription,
  collectionFooter,
  headerTitle,
  image,
  // will be used later
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flipped,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  backside,
  className,
}: PreviewCardProps) => {
  return (
    <article className={cls(styles.cardBorder, className)}>
      <div className={cls(styles.previewCard)}>
        <div className={styles.cardFace}>
          <div className={styles.previewCardHeader}>
            <div className={styles.previewCardHeaderTexts}>
              <h3 className={cls('m-0 subheading-default', styles.previewCardTitle)}>
                {headerTitle}
              </h3>
              <p className={cls('m-0', styles.previewCardDescription)}>{headerDescription}</p>
            </div>
            <DuskbreakersIcon />
          </div>
          <img src={image} className={styles.previewCardImage} />
          <footer className={styles.previewCardFooter}>
            <p className={styles.collectionTitle}>{collectionFooter}</p>
          </footer>
        </div>
      </div>
    </article>
  );
};

type UsedProps = 'headerTitle' | 'headerDescription' | 'collectionFooter';

export const GribblePreviewCard = ({
  image,
  id,
  ...rest
}: { id: number } & Omit<PreviewCardProps, UsedProps>) => {
  return (
    <PreviewCard
      {...rest}
      headerDescription="by Dusktech"
      headerTitle={`Gribble #${id}`}
      collectionFooter="Gribble Collection"
      image={image}
    />
  );
};

type EggPreviewCardProps = {
  image: string;
  level: number;
  id: number;
};

export const EggPreviewCard = ({
  level,
  id,
  image,
  ...rest
}: EggPreviewCardProps & Omit<PreviewCardProps, UsedProps>) => {
  return (
    <PreviewCard
      {...rest}
      headerDescription={`Level ${level}`}
      headerTitle={`Egg #${id}`}
      collectionFooter="Gribble Egg Collection"
      image={image}
    />
  );
};

export const CardBack = ({ className }: CardProps) => {
  return (
    <article className={cls(styles.cardBorder, className)}>
      <div className={cls(styles.previewCard, styles.logo)}>
        <Ellipse className={styles.ellipse} />
        <SquaresShape className={styles.squareShape} />
        <ShapeHorizontal className={styles.shapeHorizontal} />
        <ShapeVertical className={styles.shapeVertical} />
        <EllipseCut className={styles.ellipse} />
        <LogoBig />
      </div>
    </article>
  );
};

type EggPreviewAnimatedCardProps = {
  onAnimationFinish: () => void;
};

export const EggPreviewAnimatedCard = ({
  level,
  id,
  image,
  onAnimationFinish,
  ...rest
}: EggPreviewCardProps & Omit<PreviewCardProps, UsedProps> & EggPreviewAnimatedCardProps) => {
  const [flipped, setFlipped] = useState(false);
  const [glow, setGlow] = useState(false);
  const [secondFlip, setSecondFlip] = useState(false);
  const [showGribblePreview, setShowGribblePreview] = useState(false);
  const [shine, setShine] = useState(false);

  useEffect(() => {
    let timeouts: NodeJS.Timer[] = [];

    const flippedTimeout = setTimeout(() => setFlipped(true), 1000);
    timeouts = timeouts.concat(flippedTimeout);

    if (flipped) {
      const glowTimeout = setTimeout(() => setGlow(true), 1000);
      timeouts = timeouts.concat(glowTimeout);
    }

    if (glow) {
      const glowTimeout = setTimeout(() => {
        setGlow(false);
        setSecondFlip(true);
      }, 5000);
      timeouts = timeouts.concat(glowTimeout);
    }

    if (secondFlip) {
      const secondFlipTimeout = setTimeout(() => setShowGribblePreview(true), 600);
      timeouts = timeouts.concat(secondFlipTimeout);
    }

    if (showGribblePreview) {
      const gribbleTimeout = setTimeout(() => {
        setShine(true);
        timeouts.push(
          setTimeout(() => {
            // shine animation takes roughly 1 second
            onAnimationFinish();
          }, 1500),
        );
      }, 1000);
      timeouts = timeouts.concat(gribbleTimeout);
    }

    return () => timeouts.forEach((t) => clearTimeout(t));
  }, [onAnimationFinish, flipped, glow, secondFlip, showGribblePreview]);

  return (
    <div className={styles.animationContainer}>
      <AnimatePresence>
        <m.div
          animate={{
            rotateY: 180,
            transition: { duration: 2 },
          }}
        >
          {!flipped ? (
            <PreviewCard
              {...rest}
              headerDescription={`Level ${level}`}
              headerTitle={`Egg #${id}`}
              collectionFooter="Gribble Egg Collection"
              image={image}
            />
          ) : (
            <m.div
              animate={{
                scale: 1.2,
                transition: { duration: 1 },
              }}
            >
              {!showGribblePreview ? (
                <div className={styles.animationContainer}>
                  <CardBack
                    className={cls(glow && styles.glowAnimation, secondFlip && styles.flippedCard)}
                  />
                </div>
              ) : (
                <div className={styles.animationContainer}>
                  <m.div
                    animate={{
                      rotateY: 180,
                      opacity: 1,
                      transition: { duration: 1 },
                    }}
                    initial={{
                      rotateY: 90,
                      opacity: 0,
                    }}
                  >
                    <GribblePreviewCard
                      id={id}
                      className={cls(styles.gribbleCard, shine && styles.cardShine)}
                      image={gribblePlaceHolder}
                    />
                  </m.div>
                </div>
              )}
            </m.div>
          )}
        </m.div>
      </AnimatePresence>
    </div>
  );
};
